import { normalizeExerciseName } from '@gymcloud/mixedscript'

import type { DistanceUnits, LoggedSet, Units, Workout } from '~/data/types'
import { formatDayLabel, formatTimeOfDay } from './dates'
import { formatDistance, formatDuration, formatWeight } from './units'

/**
 * What a block holds, counted: "3 warm-ups · 5 sets". A warm-up is not a performance, so it is
 * counted apart from the working sets wherever an exercise's sets are summed up.
 */
export function blockTally(sets: readonly LoggedSet[]): string {
  const warmups = sets.filter((set) => set.isWarmup).length
  const working = sets.length - warmups
  return [
    ...(warmups > 0 ? [`${String(warmups)} ${warmups === 1 ? 'warm-up' : 'warm-ups'}`] : []),
    ...(working > 0 ? [`${String(working)} ${working === 1 ? 'set' : 'sets'}`] : []),
  ].join(' · ')
}

/**
 * The best an exercise's working sets came to, in whatever they measured: the heaviest weight
 * where anything was loaded, and otherwise the most reps, the longest hold or the farthest
 * distance. A warm-up is no performance, so it is never the best of anything.
 */
export function bestOf(
  sets: readonly LoggedSet[],
  units: Units,
  distanceUnits: DistanceUnits,
): string | null {
  const working = sets.filter((set) => !set.isWarmup)
  const top = (read: (set: LoggedSet) => number | null) => {
    const values = working.map(read).filter((value): value is number => value !== null && value > 0)
    return values.length === 0 ? null : Math.max(...values)
  }
  const weight = top((set) => set.weightKg)
  if (weight !== null) {
    return formatWeight(weight, units)
  }
  const reps = top((set) => set.reps)
  if (reps !== null) {
    return `${String(reps)} ${reps === 1 ? 'rep' : 'reps'}`
  }
  const held = top((set) => set.durationSeconds)
  if (held !== null) {
    return formatDuration(held)
  }
  const distance = top((set) => set.distanceMeters)
  return distance === null ? null : formatDistance(distance, distanceUnits)
}

/**
 * What one exercise of a workout came to, over its block on the workout's page: what it holds,
 * warm-ups counted apart, and the best its working sets reached.
 */
export function leadDetail(
  sets: readonly LoggedSet[],
  units: Units,
  distanceUnits: DistanceUnits,
): string {
  const best = bestOf(sets, units, distanceUnits)
  return [blockTally(sets), best === null ? null : `up to ${best}`]
    .filter((part): part is string => part !== null && part !== '')
    .join(' · ')
}

/** One of a workout's counts: what it counted, and what that count is called. */
export interface WorkoutStat {
  /** What is being counted, which the label only says in English and only in the plural. */
  readonly id: 'sets' | 'exercises' | 'moved' | 'reps'
  readonly value: string
  readonly label: string
  /** Whether the filters have left this count short of the workout's own. */
  readonly narrowed: boolean
}

/**
 * What a workout came to, in three counts. The first two are always the same two — how much
 * was done and how many things it was done to. The third is whichever measure the workout
 * actually carries: weight moved where anything was loaded, reps where nothing was.
 *
 * Warm-ups are no part of any of it. A warm-up is not a performance — it counts apart from the
 * working sets and appears in no total, as `/specs/app.spec.md` has it — so it is taken out
 * before anything here is counted rather than counted and then explained away. That also means
 * hiding the warm-ups moves none of these numbers, there being nothing of them in there to
 * take away.
 *
 * `shown` is what the filters have left and `all` is the whole workout, so a narrowed page
 * never passes its own total off as the workout's.
 */
export function summaryStats(
  shownWithWarmups: readonly LoggedSet[],
  allWithWarmups: readonly LoggedSet[],
  units: Units,
): WorkoutStat[] {
  const working = (sets: readonly LoggedSet[]) => sets.filter((set) => !set.isWarmup)
  const shown = working(shownWithWarmups)
  const all = working(allWithWarmups)

  const exercises = (sets: readonly LoggedSet[]) =>
    new Set(sets.map((set) => normalizeExerciseName(set.exerciseName))).size
  const volume = (sets: readonly LoggedSet[]) =>
    sets.reduce((moved, set) => moved + (set.weightKg ?? 0) * (set.reps ?? 0), 0)
  const reps = (sets: readonly LoggedSet[]) => sets.reduce((done, set) => done + (set.reps ?? 0), 0)

  const stat = (
    id: WorkoutStat['id'],
    value: string,
    total: string,
    label: string,
  ): WorkoutStat => ({
    id,
    value,
    label,
    narrowed: value !== total,
  })

  const counts: WorkoutStat[] = [
    stat(
      'sets',
      `${shown.length}`,
      `${all.length}`,
      all.length === 1 ? 'working set' : 'working sets',
    ),
    stat(
      'exercises',
      `${exercises(shown)}`,
      `${exercises(all)}`,
      exercises(all) === 1 ? 'exercise' : 'exercises',
    ),
  ]

  /*
   * Which measure the third column carries is decided by the whole workout rather than by
   * what is left of it, so hiding the warm-ups can never turn "moved" into "reps".
   */
  if (volume(all) > 0) {
    counts.push(
      stat('moved', formatWeight(volume(shown), units), formatWeight(volume(all), units), 'moved'),
    )
  } else if (reps(all) > 0) {
    counts.push(stat('reps', `${reps(shown)}`, `${reps(all)}`, 'reps'))
  }
  return counts
}

/** How long a workout ran, said the way somebody would say it out loud. */
export function spanOf(startedAt: number, finishedAt: number): string | null {
  const minutes = Math.floor((finishedAt - startedAt) / 60000)
  if (minutes < 1) {
    return null
  }
  const hours = Math.floor(minutes / 60)
  const rest = minutes % 60
  return [
    hours > 0 ? `${hours} ${hours === 1 ? 'hour' : 'hours'}` : null,
    rest > 0 ? `${rest} ${rest === 1 ? 'min' : 'mins'}` : null,
  ]
    .filter((part): part is string => part !== null)
    .join(' ')
}

/**
 * When a workout ran, and how long for once it has an end to measure against. The day leads,
 * then the time of it — the coarse before the fine, which is the order the app says every other
 * date in, and the order somebody would say this one out loud.
 */
export function workoutWhen(workout: Workout): string {
  const ran =
    workout.finishedAt !== null && workout.finishedAt > workout.startedAt
      ? spanOf(workout.startedAt, workout.finishedAt)
      : null
  return [formatDayLabel(workout.startedAt), formatTimeOfDay(workout.startedAt), ran]
    .filter((part) => part !== null)
    .join(' · ')
}

/** The notes written either side of a workout, each named by which end it was written at. */
export function workoutNotes(workout: Workout): { label: string; note: string }[] {
  return [
    { label: 'Opening notes', note: workout.openingNotes ?? '' },
    { label: 'Closing notes', note: workout.closingNotes ?? '' },
  ].filter(({ note }) => note.trim() !== '')
}

/** Whether a workout answers to what was typed — by its own name, or by anything inside it. */
export function workoutMatches(
  workout: Workout,
  sets: readonly LoggedSet[],
  wanted: string,
): boolean {
  const has = (text: string | null | undefined) => text?.toLowerCase().includes(wanted) === true
  if (has(workout.name) || has(workout.openingNotes) || has(workout.closingNotes)) {
    return true
  }
  return sets.some((set) => has(set.exerciseName) || has(set.notes))
}
